import React, { useState, useRef, useEffect } from 'react';
import './FeatureComparison.scss';

const PlanComparison = (props) => {
  const [selectedPlan, setSelectedPlan] = useState(
    props?.fields?.Vehicles?.[0]?.fields?.VariantName?.value || 'TVS iQube S'
  );
  const [showMore, setShowMore] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);
  const componentRef = useRef(null);
  const lastVisibleFeatureRef = useRef(null);

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    dataLayer.push({
      event: 'right_plan_cta_click',
      cta_text: plan,
      BrandName: 'TVS iQube',
      VariantName: 'SmartXonnect',
    });
  };

  const handleShowMore = () => {
    const isExpanding = !showMore;
    setShowMore(isExpanding);

    if (typeof window !== 'undefined') {
      setTimeout(() => {
        if (isExpanding) {
          const lastVisibleFeature = contentRef.current?.querySelector('.feature-row:nth-child(5)');
          if (lastVisibleFeature) {
            const offset = 10;
            const featureTop = lastVisibleFeature.getBoundingClientRect().top;
            const offsetPosition = window.pageYOffset + featureTop - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        } else {
          const offset = 100;
          const componentTop = componentRef.current?.getBoundingClientRect().top;
          const offsetPosition = window.pageYOffset + componentTop - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 50);
    }

    const ctaText = showMore ? 'Show Less' : 'Show More';

    dataLayer.push({
      event: 'right_plan_show_cta_interaction',
      cta_text: ctaText,
      section_name: selectedPlan,
      BrandName: 'TVS iQube',
      VariantName: 'SmartXonnect',
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [selectedPlan, showMore]);

  const selectedPlanFeatures = props?.fields?.Vehicles?.find(
    (vehicle) => vehicle.fields.VariantName.value === selectedPlan
  )?.fields?.Features;

  const initialFeatures = selectedPlanFeatures?.slice(0, 5) || [];
  const additionalFeatures = selectedPlanFeatures?.slice(5) || [];

  const advancedIconUrl = `${props?.fields?.FeatureAdvancedIcon?.value?.src}`;
  const basicIconUrl = `${props?.fields?.FeatureBasicIcon?.value?.src}`;
  const yesIconUrl = `${props?.fields?.YesIcon?.value?.src}`;
  const noIconUrl = `${props?.fields?.NoIcon?.value?.src}`;

  return (
    <div className="feature-comparion-container" ref={componentRef}>
      <div className="plan-comparison-container commonContainer" data-aos="fade-up">
        <div className="plan-comparison roboto-black">
          <h2 className="roboto-bold">{props?.fields?.Heading?.value}</h2>
          <div className="plan-selector">
            {props?.fields?.Vehicles?.map((vehicle, index) => (
              <button
                key={index}
                className={`plan-button ${
                  selectedPlan === vehicle.fields.VariantName.value ? 'selected' : ''
                }`}
                onClick={() => handlePlanSelection(vehicle.fields.VariantName.value)}
              >
                {vehicle.fields.VariantName.value}
              </button>
            ))}
          </div>
          <div className="plan-features-wrapper">
            <div className="plan-icons">
              <div className="features-header">
                <p>Features</p>
              </div>
              <div className="plan-header basic">
                <img
                  src={basicIconUrl}
                  alt={props?.fields?.FeatureBasicIcon?.value?.alt}
                  className="plan-icon"
                  loading="lazy"
                />
                <h3>Basic Plan</h3>
              </div>
              <div className="plan-header advanced">
                <img
                  src={advancedIconUrl}
                  alt={props?.fields?.FeatureAdvancedIcon?.value?.alt}
                  className="plan-icon"
                  loading="lazy"
                />
                <h3>Advanced Plan</h3>
              </div>
            </div>
            <div
              className="plan-features"
              ref={contentRef}
              style={{
                maxHeight: showMore ? `${contentHeight}px` : '400px',
                overflow: 'hidden',
                transition: 'max-height 0.5s ease-in-out',
              }}
            >
              <div className="feature-column">
                {initialFeatures.map((feature, index) => (
                  <div
                    className="feature-row"
                    key={index}
                    ref={index === 4 ? lastVisibleFeatureRef : null}
                  >
                    {feature.fields.FeatureName.value}
                  </div>
                ))}
                {additionalFeatures.map((feature, index) => (
                  <div className="feature-row" key={`additional-${index}`}>
                    {feature.fields.FeatureName.value}
                  </div>
                ))}
              </div>
              <div className="plan-column basic">
                {initialFeatures.map((feature, index) => (
                  <div className="feature-row" key={index}>
                    <img
                      src={feature.fields.Basic.value ? yesIconUrl : noIconUrl}
                      alt={
                        feature.fields.Basic.value
                          ? props?.fields?.YesIcon?.value?.alt
                          : props?.fields?.NoIcon?.value?.alt
                      }
                      className="mark-icon"
                      loading="lazy"
                    />
                  </div>
                ))}
                {additionalFeatures.map((feature, index) => (
                  <div className="feature-row" key={`additional-${index}`}>
                    <img
                      src={feature.fields.Basic.value ? yesIconUrl : noIconUrl}
                      alt={
                        feature.fields.Basic.value
                          ? props?.fields?.YesIcon?.value?.alt
                          : props?.fields?.NoIcon?.value?.alt
                      }
                      className="mark-icon"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              <div className="plan-column advanced">
                {initialFeatures.map((feature, index) => (
                  <div className="feature-row" key={index}>
                    <img
                      src={feature.fields.Advanced.value ? yesIconUrl : noIconUrl}
                      alt={
                        feature.fields.Advanced.value
                          ? props?.fields?.YesIcon?.value?.alt
                          : props?.fields?.NoIcon?.value?.alt
                      }
                      className="mark-icon"
                      loading="lazy"
                    />
                  </div>
                ))}
                {additionalFeatures.map((feature, index) => (
                  <div className="feature-row" key={`additional-${index}`}>
                    <img
                      src={feature.fields.Advanced.value ? yesIconUrl : noIconUrl}
                      alt={
                        feature.fields.Advanced.value
                          ? props?.fields?.YesIcon?.value?.alt
                          : props?.fields?.NoIcon?.value?.alt
                      }
                      className="mark-icon"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            className="show-more"
            onClick={handleShowMore}
            style={{ display: additionalFeatures.length > 0 ? 'flex' : 'none' }}
          >
            <span>{showMore ? 'View Less' : 'View All'}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              className={showMore ? 'up-arrow' : 'down-arrow'}
            >
              <path
                d="M3.5 5.5L8.5 10.5L13.5 5.5"
                stroke="#333333"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PlanComparison);
